<div class="p-(--padding-section) max-w-5xl mx-auto">
  <div class="mb-(--space-6)">
    <h1 class="text-3xl font-bold text-(color:--text-primary) mb-(--space-2)">Tests des permissions</h1>
    <p class="text-(color:--text-secondary)">
      Cette page permet de vérifier le bon fonctionnement du système de permissions. Sélectionnez un utilisateur et un ouvrage pour
      tester les différents scénarios de droits d'accès.
    </p>
  </div>

  <!-- Documentation développeur -->
  <div class="mb-(--space-8) p-(--space-6) bg-green-50 border-l-4 border-green-500 rounded-r-lg">
    <h3 class="font-semibold text-lg text-green-900 mb-(--space-3)">Guide d'implémentation de la directive hasPermission</h3>

    <div class="space-y-(--space-4) text-sm">
      <div>
        <h4 class="font-semibold text-green-800 mb-(--space-1)">Syntaxe de base</h4>
        <code class="block bg-(--surface-0) p-(--space-2) rounded-(--radius-md) border border-green-200 text-xs">
          &lt;button [hasPermission]="{{ '{' }}permissionId: 1{{ '}' }}" pButton&gt;Action&lt;/button&gt;
        </code>
      </div>

      <div>
        <h4 class="font-semibold text-green-800 mb-(--space-2)">Paramètres</h4>
        <ul class="list-disc list-inside space-y-(--space-1) text-green-900 mb-(--space-3)">
          <li><strong>permissionId</strong> (requis) - ID de la permission</li>
          <li><strong>type</strong> (optionnel) - 'read' ou 'write' (défaut: read)</li>
          <li><strong>mode</strong> (optionnel) - 'disable' ou 'hide' (défaut: hide)</li>
        </ul>
        <h4 class="font-semibold text-green-800 mb-(--space-1) text-sm">Paramètres de test (uniquement pour cette page)</h4>
        <ul class="list-disc list-inside space-y-(--space-1) text-green-900 text-sm">
          <li><strong>userId</strong> - Simule un utilisateur spécifique</li>
          <li><strong>forceCheck</strong> - Force la vérification en mode dev</li>
        </ul>
      </div>

      <div>
        <h4 class="font-semibold text-green-800 mb-(--space-2)">Liste des permissions</h4>
        <div class="bg-(--surface-0) rounded-(--radius-md) border border-green-200 overflow-hidden">
          <table class="w-full text-xs">
            <thead class="bg-green-100">
              <tr>
                <th class="px-(--space-3) py-(--space-2) text-left">ID</th>
                <th class="px-(--space-3) py-(--space-2) text-left">Nom</th>
                <th class="px-(--space-3) py-(--space-2) text-left">Module</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-green-100">
              <tr
                ><td class="px-(--space-3) py-(--space-1)">1</td><td class="px-(--space-3) py-(--space-1)">Tableau de bord</td
                ><td class="px-(--space-3) py-(--space-1)">Suivi</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">2</td><td class="px-(--space-3) py-(--space-1)">Campagnes</td
                ><td class="px-(--space-3) py-(--space-1)">Suivi</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">3</td><td class="px-(--space-3) py-(--space-1)">Mesures automatiques</td
                ><td class="px-(--space-3) py-(--space-1)">Suivi</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">4</td><td class="px-(--space-3) py-(--space-1)">Postes d'analyse</td
                ><td class="px-(--space-3) py-(--space-1)">Suivi</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">5</td><td class="px-(--space-3) py-(--space-1)">Tableaux</td
                ><td class="px-(--space-3) py-(--space-1)">Analyse</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">6</td><td class="px-(--space-3) py-(--space-1)">Graphiques</td
                ><td class="px-(--space-3) py-(--space-1)">Analyse</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">7</td
                ><td class="px-(--space-3) py-(--space-1)">Publications automatiques</td
                ><td class="px-(--space-3) py-(--space-1)">Analyse</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">8</td><td class="px-(--space-3) py-(--space-1)">Localisations</td
                ><td class="px-(--space-3) py-(--space-1)">Configuration</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">9</td><td class="px-(--space-3) py-(--space-1)">Mesures</td
                ><td class="px-(--space-3) py-(--space-1)">Configuration</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">10</td><td class="px-(--space-3) py-(--space-1)">Droits d'accès</td
                ><td class="px-(--space-3) py-(--space-1)">Configuration</td></tr
              >
              <tr
                ><td class="px-(--space-3) py-(--space-1)">11</td><td class="px-(--space-3) py-(--space-1)">Documents</td
                ><td class="px-(--space-3) py-(--space-1)">Configuration</td></tr
              >
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Informations de contexte -->
  <div class="mb-(--space-8) p-(--padding-card) bg-blue-50 border border-blue-200 rounded-(--radius-lg)">
    <h3 class="font-semibold mb-(--space-3) text-blue-900">Configuration du test</h3>

    <div class="mb-(--space-4)">
      <label for="testUserSelect" class="block font-medium text-blue-800 mb-(--space-2)">Tester en tant qu'utilisateur :</label>
      <p-select
        inputId="testUserSelect"
        [ngModel]="testUserId()"
        [options]="availableUsers"
        (onChange)="onUserSelected($event)"
        optionLabel="label"
        optionValue="value"
        placeholder="Choisir un utilisateur"
        [showClear]="false"
        [filter]="true"
        filterPlaceholder="Rechercher un utilisateur"
        class="w-full" />
      <p class="text-xs text-blue-600 mt-(--space-1)"> Simule les droits de cet utilisateur pour tous les tests ci-dessous </p>
    </div>

    <div class="mb-(--space-4)">
      <label for="testAssetSelect" class="block font-medium text-blue-800 mb-(--space-2)">Sélectionner un ouvrage :</label>
      <p-select
        inputId="testAssetSelect"
        [(ngModel)]="selectedAssetId"
        [options]="groupedAssets"
        (onChange)="onAssetSelected($event)"
        optionLabel="label"
        optionValue="value"
        optionGroupLabel="label"
        optionGroupChildren="items"
        placeholder="Choisir un ouvrage autorisé"
        [group]="true"
        [filter]="true"
        filterPlaceholder="Rechercher un ouvrage"
        [showClear]="true"
        class="w-full"
        [disabled]="!testUserId()" />
      <p class="text-xs text-blue-600 mt-(--space-1)">
        @if (testUserId() && groupedAssets.length === 0) {
          <span class="text-orange-600 font-medium">⚠️ Cet utilisateur n'a accès à aucun ouvrage</span>
        } @else if (testUserId()) {
          <span>{{ groupedAssets.length }} ouvrage(s) autorisé(s)</span>
        } @else {
          <span>Sélectionnez d'abord un utilisateur</span>
        }
      </p>
    </div>

    <div class="grid grid-cols-2 gap-(--gap-normal) text-sm pt-(--space-3) border-t border-blue-200">
      <div>
        <span class="font-medium text-blue-700">Ouvrage actuel :</span>
        <span class="ml-(--space-2) text-blue-900">{{ currentAssetId() || 'Aucun' }}</span>
      </div>
      <div>
        <span class="font-medium text-blue-700">ID utilisateur test :</span>
        <span class="ml-(--space-2) text-blue-900">{{ testUserId() || 'Non sélectionné' }}</span>
      </div>
    </div>
  </div>

  <!-- Exemples de base -->
  <h2 class="text-2xl font-bold text-(color:--text-primary) mb-(--space-4) mt-(--space-8)">Exemples de base</h2>

  <!-- Cas 1: Permission lecture simple -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">1. Permission lecture simple</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Syntaxe minimale : seul <code class="bg-(--surface-2) px-(--space-1) rounded-(--radius-md)">permissionId</code> est requis.
      L'ouvrage est détecté automatiquement depuis le contexte.
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 1{{ '}' }}" pButton&gt;Accéder&lt;/button&gt;
    </code>
    <button
      [hasPermission]="{ permissionId: PermissionScope.Dashboard, userId: testUserId() ?? undefined, forceCheck: true }"
      pButton
      label="Accéder au tableau de bord"
      class="p-button-primary"></button>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Visible si droit lecture sur "Tableau de bord" (ID 1)
    </p>
  </div>

  <!-- Cas 2: Permission écriture -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">2. Permission écriture</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Ajout de <code class="bg-(--surface-2) px-(--space-1) rounded-(--radius-md)">type: 'write'</code> pour exiger un droit
      d'écriture.
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 2, type: 'write'{{ '}' }}" pButton&gt;Modifier&lt;/button&gt;
    </code>
    <button
      [hasPermission]="{ permissionId: PermissionScope.Campaigns, type: 'write', userId: testUserId() ?? undefined, forceCheck: true }"
      pButton
      label="Modifier une campagne"
      class="p-button-success"></button>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Visible uniquement si droit écriture sur "Campagnes" (ID 2)
    </p>
  </div>

  <!-- Cas 3: Mode hide (défaut) -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">3. Mode hide (défaut) - bouton masqué</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Par défaut, un bouton sans permission disparaît complètement de l'interface.
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 999{{ '}' }}" pButton&gt;Action&lt;/button&gt;
    </code>
    <button
      [hasPermission]="{ permissionId: 999, userId: testUserId() ?? undefined, forceCheck: true }"
      pButton
      label="Action interdite (invisible)"
      class="p-button-danger"></button>
    <p class="text-xs text-orange-600 mt-(--space-2) italic">
      <strong>Attendu :</strong> Ce bouton rouge devrait être invisible (permission 999 n'existe pas)
    </p>
  </div>

  <!-- Cas 4: Mode disable -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">4. Mode disable - bouton grisé</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Avec <code class="bg-(--surface-2) px-(--space-1) rounded-(--radius-md)">mode: 'disable'</code>, le bouton reste visible mais
      grisé. Utile pour montrer qu'une fonctionnalité existe.
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 999, mode: 'disable'{{ '}' }}" pButton&gt;Action&lt;/button&gt;
    </code>
    <button
      [hasPermission]="{ permissionId: 999, mode: 'disable', userId: testUserId() ?? undefined, forceCheck: true }"
      pButton
      label="Action interdite (désactivée)"
      class="p-button-secondary"></button>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Bouton visible mais grisé avec opacité 50%
    </p>
  </div>

  <!-- Exemples réels -->
  <h2 class="text-2xl font-bold text-(color:--text-primary) mb-(--space-4) mt-(--space-8)">Exemples réels CESmon9</h2>

  <!-- Actions CRUD campagne -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">5. Actions CRUD sur campagnes</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Barre d'actions complète : consultation (lecture) et modifications (écriture)
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 2{{ '}' }}" pButton&gt;Voir&lt;/button&gt; &lt;button [hasPermission]="{{
        '{'
      }}permissionId: 2, type: 'write'{{ '}' }}" pButton&gt;Modifier&lt;/button&gt; &lt;button [hasPermission]="{{ '{' }}permissionId:
      2, type: 'write'{{ '}' }}" pButton&gt;Supprimer&lt;/button&gt;
    </code>
    <div class="flex gap-(--gap-normal)">
      <button
        [hasPermission]="{ permissionId: PermissionScope.Campaigns, userId: testUserId() ?? undefined, forceCheck: true }"
        pButton
        label="Voir détails"
        icon="pi pi-eye"
        class="p-button-outlined"></button>
      <button
        [hasPermission]="{
          permissionId: PermissionScope.Campaigns,
          type: 'write',
          userId: testUserId() ?? undefined,
          forceCheck: true,
        }"
        pButton
        label="Modifier"
        icon="pi pi-pencil"
        class="p-button-primary"></button>
      <button
        [hasPermission]="{
          permissionId: PermissionScope.Campaigns,
          type: 'write',
          userId: testUserId() ?? undefined,
          forceCheck: true,
        }"
        pButton
        label="Supprimer"
        icon="pi pi-trash"
        severity="danger"></button>
    </div>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> "Voir" visible si lecture, "Modifier" et "Supprimer" visibles si écriture
    </p>
  </div>

  <!-- Export avec mode disable -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">6. Bouton export (mode disable pour UX)</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Reste visible mais grisé - utile pour montrer qu'une fonctionnalité existe
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 2, mode: 'disable'{{ '}' }}" pButton&gt;Exporter&lt;/button&gt;
    </code>
    <button
      [hasPermission]="{
        permissionId: PermissionScope.Campaigns,
        mode: 'disable',
        userId: testUserId() ?? undefined,
        forceCheck: true,
      }"
      pButton
      label="Exporter campagnes"
      icon="pi pi-download"></button>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Toujours visible, grisé si pas de droit lecture campagnes
    </p>
  </div>

  <!-- Configuration mesures -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">7. Configuration des mesures (écriture requise)</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Actions de création et modification réservées aux utilisateurs avec droits d'écriture
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 9, type: 'write'{{ '}' }}" pButton&gt;Nouvelle&lt;/button&gt; &lt;button
      [hasPermission]="{{ '{' }}permissionId: 9, type: 'write'{{ '}' }}" pButton&gt;Modifier&lt;/button&gt;
    </code>
    <div class="flex gap-(--gap-normal)">
      <button
        [hasPermission]="{
          permissionId: PermissionScope.Measures,
          type: 'write',
          userId: testUserId() ?? undefined,
          forceCheck: true,
        }"
        pButton
        label="Nouvelle mesure"
        icon="pi pi-plus"
        class="p-button-success"></button>
      <button
        [hasPermission]="{
          permissionId: PermissionScope.Measures,
          type: 'write',
          userId: testUserId() ?? undefined,
          forceCheck: true,
        }"
        pButton
        label="Modifier mesure"
        icon="pi pi-pencil"></button>
    </div>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Visible uniquement si droit écriture sur mesures (ID 9)
    </p>
  </div>

  <!-- Navigation protégée -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">8. Navigation protégée (tableaux d'analyse)</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Lien de navigation masqué si l'utilisateur n'a pas accès au module
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 5{{ '}' }}" routerLink="/analysis/tables" pButton&gt;
    </code>
    <button
      [hasPermission]="{ permissionId: PermissionScope.Tables, userId: testUserId() ?? undefined, forceCheck: true }"
      pButton
      label="Accéder aux tableaux"
      icon="pi pi-table"
      class="p-button-outlined"></button>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Visible si droit lecture sur tableaux d'analyse (ID 5)
    </p>
  </div>

  <!-- Section complète masquée -->
  <div class="mb-(--space-6) p-(--space-6) border border-(--border) rounded-(--radius-lg) bg-(--surface-0) shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">9. Section complète masquée (documents)</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Application de la directive sur un conteneur pour masquer toute une section
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;div [hasPermission]="{{ '{' }}permissionId: 11, type: 'write'{{ '}' }}"&gt; &lt;h4&gt;Espace documents&lt;/h4&gt; &lt;button
      pButton&gt;Télécharger&lt;/button&gt; &lt;/div&gt;
    </code>
    <div
      [hasPermission]="{ permissionId: PermissionScope.Documents, type: 'write', userId: testUserId() ?? undefined, forceCheck: true }"
      class="p-(--padding-card) border-l-4 border-blue-500 bg-blue-50">
      <h4 class="font-semibold mb-(--space-2)">Espace documents</h4>
      <button pButton label="Télécharger document" icon="pi pi-upload" class="p-button-sm"></button>
    </div>
    <p class="text-xs text-(color:--text-secondary) mt-(--space-2)">
      <strong>Attendu :</strong> Section complète visible uniquement si droit écriture documents (ID 11)
    </p>
  </div>

  <!-- Tests avancés -->
  <h2 class="text-2xl font-bold text-(color:--text-primary) mb-(--space-4) mt-(--space-8)">Tests avancés</h2>

  <!-- Sécurité sans ouvrage -->
  <div class="mb-(--space-6) p-(--space-6) border border-orange-200 rounded-(--radius-lg) bg-orange-50 shadow-sm">
    <h3 class="font-semibold text-lg mb-(--space-2) text-(color:--text-primary)">10. Mode sécurisé fail-safe (absence d'ouvrage)</h3>
    <p class="text-sm text-(color:--text-secondary) mb-(--space-3)">
      Vérifie que sans ouvrage sélectionné, les permissions sont refusées par sécurité. Utilisez
      <code class="bg-(--surface-2) px-(--space-1) rounded-(--radius-md)">mode: 'disable'</code> pour voir visuellement le blocage.
    </p>
    <code class="block bg-(--surface-2) p-(--space-2) rounded-(--radius-md) text-xs mb-(--space-3)">
      &lt;button [hasPermission]="{{ '{' }}permissionId: 1, mode: 'disable'{{ '}' }}" pButton&gt;Test&lt;/button&gt;
    </code>
    <div class="flex gap-(--gap-normal) mb-(--space-3)">
      <button (click)="clearAssetContext()" pButton label="Effacer le contexte" class="p-button-warning"></button>
      <button
        [hasPermission]="{
          permissionId: PermissionScope.Dashboard,
          mode: 'disable',
          userId: testUserId() ?? undefined,
          forceCheck: true,
        }"
        pButton
        label="Test sans ouvrage"
        class="p-button-danger"></button>
      <button (click)="restoreAssetContext()" pButton label="Restaurer" class="p-button-info"></button>
    </div>
    <p class="text-xs text-(color:--text-secondary)">
      <strong>Attendu :</strong> Après effacement, le bouton rouge doit être grisé et désactivé (sécurité fail-safe)
    </p>
  </div>

  <!-- Informations utiles -->
  <div class="mt-(--space-8) p-(--padding-card) bg-(--surface-1) border border-(--border) rounded-(--radius-lg)">
    <p class="text-xs text-(color:--text-secondary)">
      Astuce : Ouvrez la console développeur (F12) pour voir les logs détaillés de vérification des permissions
    </p>
  </div>
</div>
